import {
    createContext,
    type ReactElement,
    type ReactNode,
    useContext,
    useMemo
} from "react";

/**
 * The submit action, shared with the panel that hosts it.
 *
 * Submit used to live in the page shell, below the hub. It now belongs to
 * the Review and submit section, because that is where the applicant reads
 * what they are about to file. The action itself still belongs to the hub
 * (it owns the application bundle and the refetch), so it travels here
 * rather than through every panel prop in between.
 */
export interface OnboardingSubmitValue {
    /** Undefined while a submit is in flight, or before the bundle loads. */
    submit?: () => void;
    submitting: boolean;
    /** Every applicant section is saved, so the API will accept a submit. */
    submittable: boolean;
    error: string | null;
}

const Context = createContext<OnboardingSubmitValue | null>(null);

export function OnboardingSubmitProvider({
    value,
    children
}: {
    value: OnboardingSubmitValue;
    children: ReactNode;
}): ReactElement {
    // The hub rebuilds the object each render; depend on the fields so
    // the panel below only re-renders when one actually changes.
    const { submit, submitting, submittable, error } = value;
    const memoized = useMemo<OnboardingSubmitValue>(
        () => ({ submit, submitting, submittable, error }),
        [submit, submitting, submittable, error]
    );
    return <Context.Provider value={memoized}>{children}</Context.Provider>;
}

/** `null` outside a provider — the mock-scenario dev path renders without one. */
export function useOnboardingSubmit(): OnboardingSubmitValue | null {
    return useContext(Context);
}
